// Request-a-meeting composer (Phase 5). Opens from a member's profile and lets
// the proposer offer a title, note, duration, and one or more candidate times.

import { useState } from 'react';
import { X, Plus, Trash2, CalendarPlus, MapPin, Phone, Video, MoreHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import { meetingsAPI } from '../api/client';
import DateTimeField from './DateTimeField';
import MicButton from './MicButton';
import type { MeetingMode, MeetingRequest } from '../types';

interface RequestMeetingModalProps {
  inviteeId: number;
  inviteeName: string;
  meeting?: MeetingRequest | null;
  onClose: () => void;
  onCreated?: () => void;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function toLocalInput(date: Date): string {
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromIsoToLocal(iso: string): string {
  return toLocalInput(new Date(iso));
}

function defaultSlot(): string {
  const date = new Date();
  date.setSeconds(0, 0);
  date.setHours(date.getHours() + 1);
  return toLocalInput(date);
}

function parseLocalDateTime(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
}

function apiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!data || typeof data !== 'object') return fallback;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.detail === 'string') return data.detail;
  for (const value of Object.values(data)) {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  }
  return fallback;
}

const DURATIONS = [15, 30, 45, 60, 90];

const MODES: { id: MeetingMode; label: string; hint: string; icon: typeof Video }[] = [
  { id: 'in_person', label: 'In person', hint: 'Coffee shop, office, address…', icon: MapPin },
  { id: 'phone', label: 'Phone', hint: 'Phone number or “I’ll call you”', icon: Phone },
  { id: 'video', label: 'Video', hint: 'Optional note — Meet/Teams if you connected a calendar', icon: Video },
  { id: 'other', label: 'Other', hint: 'Where or how you’ll meet', icon: MoreHorizontal },
];

export default function RequestMeetingModal({
  inviteeId,
  inviteeName,
  meeting,
  onClose,
  onCreated,
}: RequestMeetingModalProps) {
  const editing = Boolean(meeting);
  const [title, setTitle] = useState(meeting?.title || '');
  const [note, setNote] = useState(meeting?.note || '');
  const [meetingMode, setMeetingMode] = useState<MeetingMode>(meeting?.meeting_mode || 'in_person');
  const [location, setLocation] = useState(meeting?.location || '');
  const [duration, setDuration] = useState(meeting?.duration_minutes || 30);
  const [times, setTimes] = useState<string[]>(
    meeting?.proposed_times?.length ? meeting.proposed_times.map(fromIsoToLocal) : [defaultSlot()],
  );
  const [submitting, setSubmitting] = useState(false);

  const updateTime = (idx: number, value: string) => {
    setTimes((prev) => prev.map((t, i) => (i === idx ? value : t)));
  };

  const addTime = () => setTimes((prev) => [...prev, '']);
  const removeTime = (idx: number) =>
    setTimes((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please add a title.');
      return;
    }
    const rawTimes = times.map((t) => t.trim());
    const parsed = rawTimes.map(parseLocalDateTime);
    if (parsed.every((date) => !date)) {
      toast.error('Please propose at least one time.');
      return;
    }
    if (parsed.some((date, i) => rawTimes[i] && !date)) {
      toast.error('One of the proposed times is not a valid date.');
      return;
    }
    const now = new Date();
    const future = parsed.filter((date): date is Date => date !== null && date > now);
    if (future.length === 0) {
      toast.error('Proposed times must be in the future. Those slots have already passed.');
      return;
    }

    const seen = new Set<number>();
    const proposed: string[] = [];
    for (const date of future) {
      const key = date.getTime();
      if (seen.has(key)) continue;
      seen.add(key);
      proposed.push(date.toISOString());
    }

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        note: note.trim(),
        meeting_mode: meetingMode,
        location: location.trim(),
        duration_minutes: duration,
        proposed_times: proposed,
      };
      if (meeting) {
        await meetingsAPI.update(meeting.id, payload);
        toast.success(
          meeting.status === 'accepted'
            ? `Update sent to ${inviteeName} for approval.`
            : `Meeting request updated.`,
        );
      } else {
        await meetingsAPI.create({
          invitee_id: inviteeId,
          ...payload,
        });
        toast.success(`Meeting request sent to ${inviteeName}.`);
      }
      onCreated?.();
      onClose();
    } catch (err: unknown) {
      toast.error(apiError(err, 'Could not save the meeting request.'));
    } finally {
      setSubmitting(false);
    }
  };

  const minLocal = toLocalInput(new Date());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-navy flex items-center gap-2">
            <CalendarPlus size={20} />
            {editing ? `Edit meeting with ${inviteeName}` : `Request a meeting with ${inviteeName}`}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Coffee chat, mentoring, intro call…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note <span className="text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="What would you like to talk about?"
                className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
              />
              <MicButton
                value={note}
                onChange={setNote}
                className="absolute bottom-2 right-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How are you meeting?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {MODES.map((mode) => {
                const Icon = mode.icon;
                const selected = meetingMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setMeetingMode(mode.id)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                      selected
                        ? 'border-navy bg-navy/5 font-medium text-navy'
                        : 'border-gray-200 text-gray-600 hover:border-navy/30'
                    }`}
                  >
                    <Icon size={16} />
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration
              </label>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
              >
                {DURATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} minutes
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {meetingMode === 'phone' ? 'Number' : 'Location'}{' '}
                <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={MODES.find((mode) => mode.id === meetingMode)?.hint}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proposed times
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Offer one or more future options — {inviteeName.split(' ')[0]} picks one.
            </p>
            <div className="space-y-2">
              {times.map((t, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <DateTimeField
                    min={minLocal}
                    value={t}
                    onChange={(next) => updateTime(idx, next)}
                    aria-label={`Proposed time ${idx + 1}`}
                    className="flex-1"
                    inputClassName="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                  />
                  {times.length > 1 && (
                    <button
                      onClick={() => removeTime(idx)}
                      className="p-2 text-gray-400 hover:text-red-500"
                      aria-label="Remove time"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {times.length < 5 && (
              <button
                onClick={addTime}
                className="mt-2 inline-flex items-center gap-1 text-sm text-navy hover:underline"
              >
                <Plus size={16} />
                Add another time
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500">
            {meeting?.status === 'accepted'
              ? `Saving sends this back to ${inviteeName.split(' ')[0]} to accept. The current calendar event comes off until they confirm.`
              : meetingMode === 'video'
                ? 'When accepted, this is added to both calendars. A Meet or Teams link is added if you connected a calendar in Settings → Account.'
                : 'When accepted, this is added to both calendars. No video link is created for this meeting type.'}
          </p>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-navy transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
          >
            {submitting ? 'Saving…' : editing ? 'Save changes' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  );
}
